import { Box, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { useEventsContext } from '../../../../../../context/events/EventsContext';
import { EventName } from '../../../../../../services/EventTrackingService';
import { RESPELLING_TOGGLE_COPY } from '../../../../../CallToAction/sources';
import Switch from '../../../../../Inputs/Switch';

interface RespellingToggleProps {
  checked: boolean;
  onChange: (event: any) => void;
  errorMessage: string | undefined | null;
  location?: 'library' | 'editor';
  showRespellingMessage?: boolean;
}

const styles = makeStyles()(theme => ({
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  errorContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  questionMark: {
    color: theme.palette.grey[500],
    marginLeft: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: theme.spacing(1.8),
    height: theme.spacing(1.8),
    borderRadius: '50%',
    border: `1px solid ${theme.palette.grey[500]}`,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 11,
    padding: 0.5,
    '&:hover': {
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.common.white}`,
    },
  },
  tooltip: {
    '& .MuiTooltip-tooltip': {
      backgroundColor: theme.palette.grey[900],
      color: theme.palette.common.white,
      borderRadius: 0,
      marginBottom: theme.spacing(0.5),
      border: `1px solid ${theme.palette.grey[800]}`,
    },
  },
}));

const RespellingToggle = (props: RespellingToggleProps) => {
  const { checked, onChange, errorMessage, location, showRespellingMessage } =
    props;
  const { classes } = styles();
  const [eventTracking] = useEventsContext();

  const handleHelpLinkClick = () => {
    const type =
      location === 'library'
        ? EventName.HelpClickLibraryRespellingToggle
        : EventName.HelpClickEditorRespellingToggle;

    eventTracking.track({
      type,
      data: {
        action: 'linkClick',
      },
    });
  };

  return (
    <Box>
      <Box className={classes.toggleContainer}>
        <Box marginRight={1}>
          <Switch
            id="selected-replacement-respelling"
            checked={checked}
            onChange={onChange}
            inputProps={{
              'aria-labelledby': 'phoneticRespellingHeader',
              role: 'switch',
            }}
          />
        </Box>
        <Box mt={0.25}>
          <Typography variant="pSm" id="phoneticRespellingHeader">
            Phonetic Respelling
          </Typography>
        </Box>
        <Box marginTop={0.75}>
          <Tooltip
            placement="top-end"
            title={
              <Box>
                <Typography variant="pSm">
                  {RESPELLING_TOGGLE_COPY.text}{' '}
                  <a
                    href={RESPELLING_TOGGLE_COPY.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleHelpLinkClick}
                  >
                    {RESPELLING_TOGGLE_COPY.ctaText}
                  </a>
                </Typography>
              </Box>
            }
            classes={{ popper: classes.tooltip }}
          >
            <Box
              component="button"
              tabIndex={0}
              className={classes.questionMark}
            >
              ?
            </Box>
          </Tooltip>
        </Box>
      </Box>
      {showRespellingMessage && (
        <Typography variant="pMd">
          Not sounding right? Try{' '}
          <span style={{ fontWeight: 'bold' }}>Phonetic Respelling</span>
        </Typography>
      )}
      {errorMessage && (
        <Typography
          role="alert"
          variant="pMd"
          color="error"
          className={classes.errorContainer}
        >
          <Box marginRight={1} paddingTop={0.5}>
            <Icon type="warningFilled" />
          </Box>
          <Box>{errorMessage}</Box>
        </Typography>
      )}
    </Box>
  );
};

export default RespellingToggle;
